import { useSyncExternalStore } from 'react'

// index.html's splash stays at least this long, so a fast load still reads as one deliberate beat.
const MIN_MS = 1500
// Ready has to hold this long: a redirect renders a beat with no loading state before its destination's.
const SETTLE_MS = 150

// No splash at all (prerender, tests, a page not served from index.html) counts as already gone.
let splashGone = typeof document === 'undefined' || !document.getElementById('boot-splash')
const listeners = new Set<() => void>()

function markSplashGone() {
  if (splashGone) return
  splashGone = true
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** True once index.html's splash has faded away, so entrance animations play in view. */
export function useBootSplashGone(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => splashGone,
    () => true
  )
}

/** Fades out index.html's splash once MIN_MS has passed and the page has held no loading state for SETTLE_MS. */
export function dismissBootSplashWhenReady(): void {
  const splash = document.getElementById('boot-splash')
  const root = document.getElementById('root')
  if (!splash || !root) return

  const isReady = () => root.firstChild !== null && !root.querySelector('[data-page-loading]')

  const finish = () => {
    splash.remove()
    markSplashGone()
  }
  // index.html's 10s CSS fallback, which may already have finished before this script runs.
  const fallbackDone = () =>
    splash
      .getAnimations()
      .some(
        (animation) =>
          (animation as CSSAnimation).animationName === 'boot-splash-out' &&
          animation.playState === 'finished'
      )
  if (fallbackDone()) return finish()
  splash.addEventListener('animationend', (event) => {
    if (event.animationName === 'boot-splash-out') finish()
  })

  const dismiss = () => {
    // A splash the fallback already faded can't transition again, so no transitionend would fire.
    if (fallbackDone()) return finish()
    splash.classList.add('boot-splash-leaving')
    splash.addEventListener('transitionend', finish, { once: true })
  }

  let settleTimer: number | undefined
  const observer = new MutationObserver(() => checkReady())
  // Only a return to loading restarts the wait, so a page that keeps updating can't hold the splash.
  function checkReady() {
    if (!isReady()) {
      window.clearTimeout(settleTimer)
      settleTimer = undefined
      return
    }
    settleTimer ??= window.setTimeout(() => {
      settleTimer = undefined
      if (!isReady()) return
      observer.disconnect()
      dismiss()
    }, SETTLE_MS)
  }

  window.setTimeout(
    () => {
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-page-loading'],
      })
      checkReady()
    },
    Math.max(0, MIN_MS - performance.now())
  )
}
